import { FINANCE_HUB_ROUTES } from '@/navigators/routes';
import { FinanceHubStackParams } from '@/navigators/types';
import { Icons } from '@usepepper/sprout-kit-ui';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import {
  MicroProduct,
  MicroProductsSlot,
  useMicroProductsLoader,
} from '@domain/microproducts/loaders/use-microproducts-loader';
import { NavItemIcon } from '@/domain/navigation/types';
import { useMemo } from 'react';
import { useRoute } from '@react-navigation/native';

export interface FinanceHubSidebarNavItem<
  T extends keyof FinanceHubStackParams = keyof FinanceHubStackParams,
> {
  icon?: NavItemIcon;
  iconUrl?: string;
  /** Product ID — the cross-app contract (the GA "Product id"). */
  id: string;
  isActive: boolean;
  label: string;
  params?: FinanceHubStackParams[T];
  route: T;
}

/** Config cannot enable a product that this build has no phone layout for. */
type MobileSupport = 'unsupported' | 'always' | 'opt-in';

/**
 * A Finance Hub product whose screen is coded directly into the app.
 */
interface NativeProduct {
  activeRouteNames: string[];
  mobileSupport: MobileSupport;
  route: keyof FinanceHubStackParams;
}

const NATIVE_PRODUCTS: Record<string, NativeProduct> = {
  autopay: {
    activeRouteNames: [
      FINANCE_HUB_ROUTES.Autopay,
      FINANCE_HUB_ROUTES.AutopayRunPreview,
    ],
    mobileSupport: 'opt-in',
    route: FINANCE_HUB_ROUTES.Autopay,
  },
  customers: {
    activeRouteNames: [FINANCE_HUB_ROUTES.Customers],
    mobileSupport: 'opt-in',
    route: FINANCE_HUB_ROUTES.Customers,
  },
  invoices: {
    activeRouteNames: [FINANCE_HUB_ROUTES.Invoices],
    mobileSupport: 'opt-in',
    route: FINANCE_HUB_ROUTES.Invoices,
  },
};

/**
 * A business organization opts a product into phones with the `mobile_enabled` client prop on its config.
 */
const isMobileEnabled = (product: MicroProduct): boolean =>
  product.props.mobile_enabled === true;

interface UseFinanceHubSidebarNavItemsLoaderResult {
  error: Error | null;
  loading: boolean;
  navItems: FinanceHubSidebarNavItem[];
}

/**
 * Resolves a product's icon, preferring a named Sprout Kit icon and falling
 * back to its icon URL.
 */
const resolveIcon = (
  product: MicroProduct,
): { icon?: NavItemIcon; iconUrl?: string } => {
  if (product.displayIconName) {
    const icons = Icons as Record<string, NavItemIcon | undefined>;
    const icon = icons[product.displayIconName];
    if (icon) {
      return { icon };
    }
  }
  return { iconUrl: product.displayIconUrl ?? undefined };
};

const toNavItem = (
  product: MicroProduct,
  native: NativeProduct,
  currentRouteName: string,
): FinanceHubSidebarNavItem => ({
  ...resolveIcon(product),
  id: product.id,
  isActive: native.activeRouteNames.includes(currentRouteName),
  label: product.displayName,
  route: native.route,
});

/**
 * Loads the Finance Hub sidebar from the microproducts config
 * ({@link MicroProductsSlot.FINANCE_HUB_SIDEBAR}). Native-only: products
 * without a {@link NATIVE_PRODUCTS} entry are dropped (no SPA support yet).
 */
export const useFinanceHubSidebarNavItemsLoader =
  (): UseFinanceHubSidebarNavItemsLoaderResult => {
    const { layoutSize } = useLayoutContext();
    const isSmallLayout = layoutSize === LayoutSize.SMALL;
    const route = useRoute();

    const { slots, loading, error } = useMicroProductsLoader();

    const navItems = useMemo<FinanceHubSidebarNavItem[]>(() => {
      const sidebarSlot = slots.find(
        (s) => s.slot === MicroProductsSlot.FINANCE_HUB_SIDEBAR,
      );
      const products = sidebarSlot?.products ?? [];
      const currentRouteName = route.name;

      return products
        .map((product) => {
          const native = NATIVE_PRODUCTS[product.id];
          if (!native) {
            return null;
          }
          // Native: hidden on mobile unless the app supports it there or config opts in.
          if (
            isSmallLayout &&
            (native.mobileSupport === 'unsupported' ||
              (native.mobileSupport === 'opt-in' && !isMobileEnabled(product)))
          ) {
            return null;
          }
          return toNavItem(product, native, currentRouteName);
        })
        .filter((item): item is FinanceHubSidebarNavItem => item !== null);
    }, [slots, isSmallLayout, route.name]);

    return {
      error: error ?? null,
      loading,
      navItems,
    };
  };
